/**
 * Workflows, the apps they open in, the Scout data they take in, and what an
 * event uses.
 *
 * The admin API routes are in `omnium_admin/routers/workflows.py` and
 * `integrations.py`. The small helpers at the bottom turn those rows into words
 * for the screens.
 */

import { apiFetch } from "@/lib/api/auth";
import { ApiError, requestJson } from "@/lib/api/client";
import { getAdminApiBaseUrl } from "@/lib/api/config";
import { displayName } from "@/lib/format";

export type Role = "score" | "operate";

export type AppLink = {
  appId: string;
  code: string;
  name: string;
  url: string;
  kind: string;
  role: string;
  isDefault: boolean;
};

export type WorkflowRow = {
  id: string;
  code: string;
  name: string;
  /** `sport`, `all_sports` or `event_kind`. */
  ownerKind: string;
  sportCode: string | null;
  eventKind: string | null;
  /** `fixture` or `event`. */
  subject: string;
  purpose: string;
  /** `fold` for a scoring desk; `apply` for Results and Games operations. */
  mode: string;
  isDefault: boolean;
  description: string | null;
  readModels: string[];
  active: boolean;
  /** The published version. `null` until it is published. */
  version: number | null;
  apps: AppLink[];
};

/** One field of an import command, in the order the screens show them. */
export type ImportField = {
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
  hint?: string;
};

export type CommandInfo = {
  code: string;
  label: string | null;
  description: string | null;
  /** Only an integration may send it. */
  imports: boolean;
  fields: ImportField[];
  /** The input's JSON schema. */
  input: Record<string, unknown>;
};

export type WorkflowDetail = WorkflowRow & { commands: CommandInfo[] };

export type AppRow = {
  id: string;
  code: string;
  name: string;
  url: string;
  kind: string;
  sportCode: string | null;
  description: string | null;
  appliesTo: string[];
  workflows: { code: string; name: string; role: string; isDefault: boolean }[];
};

export type NewApp = {
  code: string;
  name: string;
  url: string;
  kind: "scoring" | "operations";
  sportCode?: string | null;
  description?: string | null;
};

export type EventChoice = {
  subject: string;
  sportCode: string | null;
  workflow: string;
  workflowName: string;
};

export type EventWorkflows = {
  eventCode: string;
  eventName: string;
  eventType: string;
  choices: EventChoice[];
  /** Every workflow this event could choose. */
  offered: WorkflowRow[];
};

/** One integration, as much as the workflow screens show of it. */
export type IntegrationBrief = {
  code: string;
  name: string;
  workflow: string;
  importCommand: string;
  eventCode: string | null;
  eventName: string | null;
  /** `setting_up`, `on`, `paused` or `off`. */
  state: string;
  pauseReason?: string | null;
  /** `written`, `nothing_new`, `failed` or `paused_by_guard`. */
  lastOutcome: string | null;
  lastRunAt: string | null;
  lastCheckedAt?: string | null;
  checkSeconds?: number | null;
  openAnswers: number;
  /** Scout tells omnium when it has new data. */
  hasDoorbell: boolean;
};

export type DraftLink = { appId: string; role: Role; isDefault: boolean };

// --------------------------------------------------------------------------- calls

const enc = encodeURIComponent;

function json(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

export function listWorkflows(
  query: { sport?: string; purpose?: string } = {},
  f: typeof fetch = fetch,
): Promise<WorkflowRow[]> {
  const params = new URLSearchParams();
  if (query.sport) params.set("sport", query.sport);
  if (query.purpose) params.set("purpose", query.purpose);
  const suffix = params.size > 0 ? `?${params}` : "";
  return requestJson<WorkflowRow[]>(`/workflows${suffix}`, undefined, f);
}

export const getWorkflow = (code: string, f: typeof fetch = fetch) =>
  requestJson<WorkflowDetail>(`/workflows/${enc(code)}`, undefined, f);

/**
 * Every workflow with its commands, so a screen can show the imports each one
 * takes. A workflow whose commands do not load still shows, with none.
 */
export async function listWorkflowDetails(
  query: { sport?: string; purpose?: string } = {},
  f: typeof fetch = fetch,
): Promise<WorkflowDetail[]> {
  const rows = await listWorkflows(query, f);
  const details = await Promise.allSettled(rows.map((row) => getWorkflow(row.code, f)));
  return rows.map((row, index) => {
    const found = details[index];
    return found.status === "fulfilled" ? found.value : { ...row, commands: [] };
  });
}

export const setWorkflowApps = (code: string, links: DraftLink[], f: typeof fetch = fetch) =>
  requestJson<WorkflowDetail>(`/workflows/${enc(code)}/apps`, json("PUT", { apps: links }), f);

export const listApps = (f: typeof fetch = fetch) => requestJson<AppRow[]>("/apps", undefined, f);

export const registerApp = (app: NewApp, f: typeof fetch = fetch) =>
  requestJson<AppRow>("/apps", json("POST", app), f);

/** Unregister an app. The server answers 204 with no body. */
export async function removeApp(id: string, f: typeof fetch = fetch): Promise<void> {
  const response = await apiFetch(
    `${getAdminApiBaseUrl()}/apps/${enc(id)}`,
    { method: "DELETE" },
    f,
  );
  if (response.ok) return;
  let message = `omnium did not remove the app (${response.status})`;
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") message = body.detail;
  } catch {
    // Not JSON: keep the message above.
  }
  throw new ApiError(message, response.status, []);
}

export const listIntegrationBriefs = (f: typeof fetch = fetch) =>
  requestJson<IntegrationBrief[]>("/integrations", undefined, f);

export const getEventWorkflows = (code: string, f: typeof fetch = fetch) =>
  requestJson<EventWorkflows>(`/events/${enc(code)}/workflows`, undefined, f);

export const chooseEventWorkflow = (
  code: string,
  choice: { subject: "event" | "fixture"; sport: string | null; workflow: string },
  f: typeof fetch = fetch,
) => requestJson<EventWorkflows>(`/events/${enc(code)}/workflows`, json("PUT", choice), f);

export function undoEventWorkflow(
  code: string,
  target: { subject: "event" | "fixture"; sport: string | null },
  f: typeof fetch = fetch,
): Promise<EventWorkflows> {
  const params = new URLSearchParams({ subject: target.subject });
  if (target.sport) params.set("sport", target.sport);
  return requestJson<EventWorkflows>(
    `/events/${enc(code)}/workflows?${params}`,
    { method: "DELETE" },
    f,
  );
}

// --------------------------------------------------------------------------- links

/** Where "Connect Scout data" goes: the new-integration screen, with the
 *  workflow and its import command already picked. */
export function connectScoutHref(workflow: string, command: string): string {
  return `/integrations/new?${new URLSearchParams({ workflow, command })}`;
}

export function integrationHref(code: string): string {
  return `/integrations/${enc(code)}`;
}

// --------------------------------------------------------------------------- words

export type GroupKey = "scoring" | "results" | "operations" | "other";

export type WorkflowGroup = { key: GroupKey; title: string; note: string; rows: WorkflowRow[] };

const GROUPS: Omit<WorkflowGroup, "rows">[] = [
  {
    key: "scoring",
    title: "Scoring for one sport",
    note: "Each sport can have its own. A scoring desk uses it to record a match as it is played.",
  },
  {
    key: "results",
    title: "Results for every sport",
    note: "Works for a fixture in any sport: its status, start, venue, sides, scores and result.",
  },
  {
    key: "operations",
    title: "Running an event",
    note: "Runs a whole event, such as the Asian Games: the medal table, the players and the schedule.",
  },
  { key: "other", title: "Other", note: "Workflows that fit none of the groups above." },
];

export function groupOf(row: Pick<WorkflowRow, "subject" | "ownerKind">): GroupKey {
  if (row.subject === "event") return "operations";
  if (row.ownerKind === "all_sports") return "results";
  if (row.ownerKind === "sport") return "scoring";
  return "other";
}

/** Every workflow in its group, in the order the page shows them. Empty groups are left out. */
export function groupWorkflows<T extends WorkflowRow>(rows: T[]): (Omit<WorkflowGroup, "rows"> & { rows: T[] })[] {
  return GROUPS.map((group) => ({
    ...group,
    rows: rows.filter((row) => groupOf(row) === group.key),
  })).filter((group) => group.rows.length > 0);
}

export function ownerLabel(row: Pick<WorkflowRow, "ownerKind" | "sportCode" | "eventKind">): string {
  if (row.ownerKind === "sport") return row.sportCode ? displayName(row.sportCode) : "One sport";
  if (row.ownerKind === "all_sports") return "Every sport";
  if (row.ownerKind === "event_kind") return `${displayName(row.eventKind ?? "event")} events`;
  return row.ownerKind;
}

export function modeLabel(mode: string): string {
  if (mode === "fold") return "Scores play by play";
  if (mode === "apply") return "Edits records";
  return mode;
}

export function subjectLabel(subject: string): string {
  if (subject === "fixture") return "One fixture";
  if (subject === "event") return "A whole event";
  return subject;
}

/** "one fixture" or "a whole event", to follow "Opens for". */
export function opensFor(subject: string): string {
  return subjectLabel(subject).toLowerCase();
}

/** One sentence on what a workflow does, for someone who has never seen it. */
export function purposeSentence(
  row: Pick<WorkflowRow, "description" | "subject" | "ownerKind" | "sportCode" | "mode">,
): string {
  if (row.description) return row.description;
  switch (groupOf(row)) {
    case "scoring":
      return `Records ${row.sportCode ? displayName(row.sportCode).toLowerCase() : "a match"} play by play, one fixture at a time.`;
    case "results":
      return "Sets a fixture's status, start, venue, sides, scores and result, in any sport.";
    case "operations":
      return "Runs a whole event: its medal table, players and schedule.";
    default:
      return `${modeLabel(row.mode)}.`;
  }
}

/** The workflow this one was made from, e.g. `cricket` for `cricket.hundred`. */
export function baseWorkflow<T extends Pick<WorkflowRow, "code">>(row: Pick<WorkflowRow, "code">, rows: T[]): T | null {
  const dot = row.code.indexOf(".");
  if (dot <= 0) return null;
  const base = row.code.slice(0, dot);
  return rows.find((other) => other.code === base) ?? null;
}

export function roleLabel(role: string): string {
  if (role === "score") return "Score";
  if (role === "operate") return "Operate";
  return role;
}

/** What an app does for a workflow, as a verb: "scores", "operates". */
export function roleDoing(role: string): string {
  if (role === "score") return "scores";
  if (role === "operate") return "operates";
  return role;
}

export const ROLE_HELP: Record<Role, string> = {
  score: "A scoring desk. It opens on one fixture and records play as it happens.",
  operate:
    "An operations app. It edits records: a fixture's result, or an event's medal table and schedule.",
};

export function appKindLabel(kind: string): string {
  if (kind === "scoring") return "Scoring desk";
  if (kind === "operations") return "Operations app";
  return displayName(kind);
}

/** Whether an app may be used with a workflow: a desk built for one sport only
 *  fits that sport's workflows and the ones for every sport. */
export function appFits(app: Pick<AppRow, "sportCode">, workflow: Pick<WorkflowRow, "sportCode">): boolean {
  return !app.sportCode || !workflow.sportCode || app.sportCode === workflow.sportCode;
}

export function importCommands(detail: Pick<WorkflowDetail, "commands">): CommandInfo[] {
  return detail.commands.filter((command) => command.imports);
}

/** "Import medal table", from a command's label or its code. */
export function commandTitle(command: Pick<CommandInfo, "code" | "label">): string {
  const text = command.label ?? command.code;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export type Tone = "neutral" | "good" | "warn" | "bad";

export type HealthIcon = "pencil" | "check" | "clock" | "question" | "pause" | "cross" | "off";

export type Health = { word: string; tone: Tone; icon: HealthIcon };

/**
 * How an integration is doing, in the same words as the integration screens.
 *
 * "Late" means nothing was checked or run for twice its check time.
 */
export function integrationHealth(
  row: Pick<IntegrationBrief, "state" | "lastOutcome" | "lastRunAt" | "openAnswers"> &
    Partial<Pick<IntegrationBrief, "lastCheckedAt" | "checkSeconds">>,
  now: number = Date.now(),
): Health {
  const guard: Health = { word: "Paused by a guard", tone: "bad", icon: "pause" };
  if (row.state === "setting_up") return { word: "Setting up", tone: "neutral", icon: "pencil" };
  if (row.state === "off") return { word: "Off", tone: "neutral", icon: "off" };
  if (row.state === "paused") {
    return row.lastOutcome === "paused_by_guard" ? guard : { word: "Paused", tone: "neutral", icon: "pause" };
  }
  if (row.state !== "on") return { word: displayName(row.state), tone: "neutral", icon: "pause" };
  if (row.openAnswers > 0) return { word: "Needs your answer", tone: "warn", icon: "question" };
  if (row.lastOutcome === "failed") return { word: "Failing", tone: "bad", icon: "cross" };
  if (row.lastOutcome === "paused_by_guard") return guard;
  const times = [row.lastRunAt, row.lastCheckedAt]
    .filter((value): value is string => Boolean(value))
    .map((value) => Date.parse(value))
    .filter((value) => !Number.isNaN(value));
  if (times.length === 0) return { word: "No run yet", tone: "neutral", icon: "clock" };
  if (row.checkSeconds && now - Math.max(...times) > row.checkSeconds * 2000) {
    return { word: "Late", tone: "warn", icon: "clock" };
  }
  return { word: "On time", tone: "good", icon: "check" };
}

/** "5 minutes ago", "3 hours ago", "2 days ago". */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (Number.isNaN(seconds)) return "at an unknown time";
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/** "every 10 minutes", "every day". */
export function everyWords(seconds: number): string {
  if (seconds % 86400 === 0) return seconds === 86400 ? "every day" : `every ${seconds / 86400} days`;
  if (seconds % 3600 === 0) return seconds === 3600 ? "every hour" : `every ${seconds / 3600} hours`;
  if (seconds % 60 === 0) return seconds === 60 ? "every minute" : `every ${seconds / 60} minutes`;
  return `every ${seconds} seconds`;
}

/** What a version's state means, for a person. */
export function versionWords(status: string): { word: string; tone: Tone; note: string } {
  if (status === "PUBLISHED") {
    return {
      word: "Published",
      tone: "good",
      note: "Fixtures use this one. It does not change here: a change in the plugin arrives as a new version.",
    };
  }
  if (status === "DRAFT") return { word: "Draft", tone: "warn", note: "Not used by any fixture yet." };
  if (status === "ARCHIVED") {
    return {
      word: "Replaced",
      tone: "neutral",
      note: "A newer version took over. It is kept as the record of what it scored.",
    };
  }
  return { word: displayName(status.toLowerCase()), tone: "neutral", note: "" };
}

export function linksOf(row: Pick<WorkflowRow, "apps">): DraftLink[] {
  return row.apps.map((app) => ({
    appId: app.appId,
    role: app.role === "operate" ? "operate" : "score",
    isDefault: app.isDefault,
  }));
}

/** Why a list of app links cannot be saved, or `null` when it can. Same rules as the server. */
export function linksProblem(links: DraftLink[], subject: string): string | null {
  if (subject === "event" && links.some((link) => link.role !== "operate")) {
    return "This workflow runs a whole event, so its apps can only operate it.";
  }
  const seen = new Set<string>();
  for (const link of links) {
    const key = `${link.appId}/${link.role}`;
    if (seen.has(key)) return "The same app is listed twice with the same role.";
    seen.add(key);
  }
  const defaults = links.filter((link) => link.isDefault).map((link) => link.role);
  if (defaults.length !== new Set(defaults).size) {
    return "Only one app can be the default for each role.";
  }
  return null;
}

/** Whether two lists of links say the same thing, in any order. */
export function sameLinks(a: DraftLink[], b: DraftLink[]): boolean {
  const text = (links: DraftLink[]) =>
    links.map((link) => `${link.appId}/${link.role}/${link.isDefault}`).sort().join("|");
  return text(a) === text(b);
}

/** How one app is used by a workflow in a list of links. */
export type AppUse = "none" | Role | "both";

export function appUse(links: DraftLink[], appId: string): AppUse {
  const roles = new Set(links.filter((link) => link.appId === appId).map((link) => link.role));
  if (roles.has("score") && roles.has("operate")) return "both";
  if (roles.has("score")) return "score";
  if (roles.has("operate")) return "operate";
  return "none";
}

/** Clear the default on other apps that share a role with this app's default. */
function oneDefault(links: DraftLink[], appId: string): DraftLink[] {
  const roles = new Set(
    links.filter((link) => link.appId === appId && link.isDefault).map((link) => link.role),
  );
  return links.map((link) =>
    link.appId !== appId && roles.has(link.role) ? { ...link, isDefault: false } : link,
  );
}

/** The links with one app used as `use`. It keeps being the default if it was. */
export function withUse(links: DraftLink[], appId: string, use: AppUse): DraftLink[] {
  const wasDefault = links.some((link) => link.appId === appId && link.isDefault);
  const roles: Role[] = use === "none" ? [] : use === "both" ? ["score", "operate"] : [use];
  const next = [
    ...links.filter((link) => link.appId !== appId),
    ...roles.map((role) => ({ appId, role, isDefault: wasDefault })),
  ];
  return wasDefault ? oneDefault(next, appId) : next;
}

/** The links with one app made the default, or not. Only one app is the default per role. */
export function withDefault(links: DraftLink[], appId: string, on: boolean): DraftLink[] {
  const next = links.map((link) => (link.appId === appId ? { ...link, isDefault: on } : link));
  return on ? oneDefault(next, appId) : next;
}

export type ChoiceTarget = { subject: "event" | "fixture"; sport: string | null };

export function targetKey(target: ChoiceTarget): string {
  return target.subject === "event" ? "event" : `fixture:${target.sport ?? ""}`;
}

export function parseTarget(key: string): ChoiceTarget {
  if (key === "event") return { subject: "event", sport: null };
  const sport = key.startsWith("fixture:") ? key.slice("fixture:".length) : "";
  return { subject: "fixture", sport: sport || null };
}

export function choiceTarget(choice: Pick<EventChoice, "subject" | "sportCode">): ChoiceTarget {
  return { subject: choice.subject === "event" ? "event" : "fixture", sport: choice.sportCode };
}

export function targetLabel(target: { subject: string; sport?: string | null; sportCode?: string | null }): string {
  const sport = target.sport ?? target.sportCode ?? null;
  if (target.subject === "event") return "The event itself";
  return sport ? `${displayName(sport)} fixtures` : "Fixtures in every sport";
}

/** The workflows that fit a target: its subject, and for one sport that sport's own or the every-sport ones. */
export function offeredFor(offered: WorkflowRow[], target: ChoiceTarget): WorkflowRow[] {
  return offered.filter((row) => {
    if (row.subject !== target.subject) return false;
    if (target.subject === "event") return true;
    if (row.ownerKind === "all_sports") return true;
    return target.sport !== null && row.sportCode === target.sport;
  });
}

/** Why a new app cannot be registered yet, or `null` when it can. Same rules as the server. */
export function newAppProblem(app: NewApp): string | null {
  if (!app.name.trim()) return "Give the app a name.";
  if (!/^[a-z0-9][a-z0-9-]{1,63}$/.test(app.code.trim())) {
    return "The short code needs 2 to 64 small letters, numbers or dashes, and cannot start with a dash.";
  }
  try {
    const url = new URL(app.url.trim());
    if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error("not http");
  } catch {
    return "The address must be a full web address that starts with http:// or https://.";
  }
  return null;
}

/** A short code made from a name: "Operations console" → "operations-console". */
export function codeFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}
